import React, { useCallback, useMemo } from 'react';
import { timeAndExpensesConfig } from 'finance/timeAndExpenses';
import { useFeatureLabel } from 'utils/hooks/useFeatureLabel';
import { useDarkly } from 'integrations/launchDarkly/legacy/useDarkly';
import { filterNotEmpty } from 'utils/data/listHelpers';
import { useTenantCode, useTenantConfig } from 'utils/hooks/useTenantConfig';
import { matchPath, useLocation } from 'react-router';
import { isSettingVisible, isWorkRequestVisible } from 'common/auth/Authorize';
import { useTaeFlags } from '@oc/pages/settings/TimesheetSettings/useTaeFlags';
import { Icon } from '@beeline.com/bp-design-system';
import { LaunchDarklyFeatureFlagKeys } from 'integrations/launchDarkly/legacy/LaunchDarklyFeatureFlags.types';
import { useLoggedInUser } from 'src/utils/hooks/useLoggedInUser';
import { useSmartformPermissionHelper } from 'src/components/smartforms/useSmartformPermissionHelper';
import { TaskNotificationsIcon } from '../header/tasks/TaskNotificationsIcon';
import { MenuItem, MenuNode, MenuPermission } from './Menu.type';
import { AuthUser } from 'src/providers/User/AuthUserModels';
import { useTaskNotifications } from 'src/utils/hooks/data/useTasks';

export const useMenuItems = (user: AuthUser) => {
  const showWorkRequestsMenuItem = isWorkRequestVisible(user);
  const { getLabel } = useFeatureLabel();
  const authUser = useLoggedInUser();
  const darkly = useDarkly();
  const uiConfig = useTenantConfig()?.features;
  const location = useLocation();
  const tenantCode = useTenantCode();
  const isAllTasks = !!matchPath(location.pathname, { path: `/${tenantCode}/all-tasks` });
  const isSettingEnabled = isSettingVisible(authUser!);
  const { helperEnabled } = useSmartformPermissionHelper();
  const { invoices, tae } = useTaeFlags();
  const { tasksToReviewCount } = useTaskNotifications();

  const checkPermission = useCallback(
    (permission: MenuPermission) =>
      ['PERMIT', 'UNSURE'].includes(user.sitePermissions[permission.name]?.[permission.action]),
    [user.sitePermissions]
  );

  const menuItems = useMemo(
    (): Omit<MenuNode, 'text' | 'isLastFromGroup'>[] =>
      filterNotEmpty([
        {
          icon: <Icon name="home" color="colorOnSurfaceVariant" />,
          messageId: 'navmenu.treemap.dashboard',
          id: MenuItem.myUtmost,
          group: 'utmost'
        },

        isAllTasks
          ? {
              icon: <TaskNotificationsIcon tasksToReviewCount={tasksToReviewCount} />,
              messageId: 'navmenu.tasks.link',
              id: MenuItem.allTasks, // Select All Tasks on Tasks Page.
              featureId: 'routes-tasks',
              group: 'utmost'
            }
          : {
              icon: <TaskNotificationsIcon tasksToReviewCount={tasksToReviewCount} />,
              messageId: 'navmenu.tasks.link',
              id: MenuItem.tasks, // Select My Tasks on Tasks Page.
              featureId: 'routes-tasks',
              group: 'utmost',
              useMessageIdFirst: true
            },
        {
          icon: <Icon name="data_extracts_custom" color="colorOnSurfaceVariant" />,
          messageId: 'navmenu.dataExtract.link',
          id: MenuItem.dataExtracts,
          group: 'utmost',
          darklyFlagKey: LaunchDarklyFeatureFlagKeys.dataConnectorExtracts,
          permission: {
            name: 'DATA_EXTRACT',
            action: 'READ'
          }
        },
        {
          icon: <Icon name="reports_custom" color="colorOnSurfaceVariant" />,
          group: 'utmost',
          messageId: 'reports.reports',
          id: MenuItem.reports
        },
        {
          icon: <Icon name="query_stats" color="colorOnSurfaceVariant" />,
          group: 'utmost',
          messageId: 'navmenu.workers.dashboards',
          id: MenuItem.dashboards,
          reverseDarkly: true,
          featureId: 'dashboards',
          permission: {
            name: 'DASHBOARDS',
            action: 'READ'
          }
        },

        {
          icon: <Icon name="pie_chart" color="colorOnSurfaceVariant" />,
          group: 'utmost',
          messageId: 'navmenu.trendingAnalytics',
          id: MenuItem.analyticsDashboards,
          darklyFlagKey: LaunchDarklyFeatureFlagKeys.analyticsDashboards,
          permission: {
            name: 'DASHBOARDS',
            action: 'READ'
          }
        },
        {
          icon: <Icon name="lan" color="colorOnSurfaceVariant" />,
          messageId: 'navmenu.chart.link',
          id: MenuItem.orgNavigator,
          featureId: 'org-chart',
          group: 'utmost'
        },
        {
          icon: <Icon name="work" color="colorOnSurfaceVariant" />,
          messageId: 'navmenu.engagements.link',
          useMessageIdFirst: true,
          id: MenuItem.engagements,
          featureId: 'engagement-hub',
          group: 'hub',
          permission: {
            name: 'PERSON',
            action: 'READ'
          }
        },
        {
          icon: <Icon name="track_changes" color="colorOnSurfaceVariant" />,
          messageId: 'navmenu.programAlerts.link',
          id: MenuItem.programAlerts,
          useMessageIdFirst: true,
          featureId: 'program-alerts-detection',
          group: 'hub',
          permission: {
            name: 'COMPLIANCE_INSTANCE',
            action: 'READ'
          }
        },
        {
          icon: <Icon name="groups" color="colorOnSurfaceVariant" />,
          messageId: 'navmenu.workers.link',
          id: MenuItem.workers,
          featureId: 'hub',
          group: 'hub',
          permission: {
            name: 'PERSON',
            action: 'READ'
          }
        },
        {
          icon: <Icon name="store" color="colorOnSurfaceVariant" />,
          messageId: 'navmenu.suppliers.link',
          id: MenuItem.suppliers,
          featureId: 'hub',
          group: 'hub',
          permission: {
            name: 'SUPPLIER',
            action: 'READ'
          }
        },
        {
          icon: <Icon name="contract" color="colorOnSurfaceVariant" />,
          messageId: 'navmenu.contracts.link',
          id: MenuItem.contracts,
          useMessageIdFirst: true,
          featureId: 'sourcing',
          group: 'hub',
          subFeatureId: 'contract',
          permission: {
            name: 'CONTRACT',
            action: 'READ'
          }
        },
        {
          icon: <Icon name="assignment" color="colorOnSurfaceVariant" />,
          messageId: 'navmenu.projects.link',
          id: MenuItem.projects,
          darklyFlagKey: LaunchDarklyFeatureFlagKeys.enterpriseProjects,
          group: 'hub',
          permission: {
            name: 'PROJECT',
            action: 'READ'
          }
        },
        {
          icon: <Icon name="documents_check_custom" color="colorOnSurfaceVariant" />,
          messageId: 'navmenu.workRequests.link',
          id: MenuItem.workRequests,
          featureId: 'sourcing',
          group: 'sourcing',
          useMessageIdFirst: true,
          show: showWorkRequestsMenuItem,
          subFeatureId: 'work-request',
          permission: {
            name: 'WORK_REQUEST',
            action: 'READ'
          }
        },
        {
          icon: <Icon name="document_candidate_search_custom" color="colorOnSurfaceVariant" />,
          messageId: 'navmenu.jobPostings.link',
          id: MenuItem.jobPostings,
          featureId: 'sourcing',
          group: 'sourcing',
          useMessageIdFirst: true,
          subFeatureId: 'job-posting',
          permission: {
            name: 'WORK_REQUEST',
            action: 'READ'
          },
          subPermission: {
            name: 'JOB_REQUEST',
            action: 'READ'
          }
        },
        {
          icon: <Icon name="job_application_custom" color="colorOnSurfaceVariant" />,
          messageId: 'navmenu.jobApplications.link',
          id: MenuItem.jobApplications,
          featureId: 'sourcing',
          group: 'sourcing',
          useMessageIdFirst: true,
          subFeatureId: 'job-application',
          permission: {
            name: 'JOB_APPLICATION',
            action: 'READ'
          }
        },
        tae && {
          icon: <Icon name="time_expense_custom" color="colorOnSurfaceVariant" />,
          messageId: timeAndExpensesConfig.title,
          id: MenuItem.timeAndExpenses,
          group: 'finance',
          useMessageIdFirst: true,
          permission: {
            name: 'TIMESHEET',
            action: 'READ'
          }
        },
        invoices.enabled && {
          icon: <Icon name="invoice_custom" color="colorOnSurfaceVariant" />,
          messageId: 'navmenu.invoices.link',
          id: MenuItem.invoicing,
          group: 'finance',
          featureId: 'invoicing',
          permission: {
            name: 'INVOICE',
            action: 'READ'
          }
        },
        isSettingEnabled
          ? {
              icon: <Icon name="settings" color="colorOnSurfaceVariant" />,
              messageId: 'navmenu.settings.link',
              id: MenuItem.settings,
              group: 'settings'
            }
          : undefined,
        {
          icon: <Icon name="person" color="colorOnSurfaceVariant" />,
          messageId: 'headermenu.myAccount.label',
          id: MenuItem.myAccount,
          style: { marginTop: 'auto' },
          group: 'settings',
          permission: {
            name: 'USER',
            action: 'READ'
          }
        }
      ]),
    [invoices.enabled, isAllTasks, isSettingEnabled, showWorkRequestsMenuItem, tasksToReviewCount, tae]
  );

  const filteredMenuItems = useMemo(
    () =>
      menuItems
        .map((item, index, allItems) => {
          const nextItem = allItems[index + 1];
          return {
            ...item,
            text: getLabel({
              featureId: item.featureId,
              messageId: item.messageId,
              subFeatureId: item.subFeatureId,
              useMessageIdFirst: item.useMessageIdFirst
            }),
            isLastFromGroup: nextItem ? nextItem.group !== item.group : false
          };
        })
        // FILTER based on uiConfig
        .filter(
          ({ featureId, permission, subPermission, darklyFlagKey, subFeatureId, show }) =>
            (!featureId || uiConfig?.[featureId]?.enabled === true) &&
            (!subFeatureId ||
              (featureId &&
                uiConfig?.[featureId].orderedFeatures.some(
                  (orderedFeature) => orderedFeature.name === subFeatureId && orderedFeature.enabled
                ))) &&
            (!darklyFlagKey || darkly[darklyFlagKey]) &&
            (!permission || helperEnabled || checkPermission(permission)) &&
            (!subPermission || checkPermission(subPermission)) &&
            (typeof show === 'undefined' || show),
          []
        ),
    [darkly, getLabel, menuItems, checkPermission, uiConfig, helperEnabled]
  );

  return {
    menuItems: filteredMenuItems,
    checkPermission
  };
};
